<template>
    <DefaultLayout
        v-bind="plainAttrs"
        :id="id"
        class="TextareaInput"
        input-container-class="TextareaInput__inputContainer"
        :class="{ 'TextareaInput--error': error, 'TextareaInput--disabled': disabled }"
        :label="label"
        :required="required"
        :error="error"
    >
        <template #default="{ id }">
            <textarea
                :id="id"
                class="TextareaInput__field"
                :value="value"
                :placeholder="placeholder"
                :disabled="disabled"
                :rows="rows"
                :maxlength="maxlength"
                v-on="listeners"
            />
        </template>
        <template
            v-for="name in DEFAULT_LAYOUT_SLOT_NAMES"
            #[name]
        >
            <slot :name="name" />
        </template>
    </DefaultLayout>
</template>

<style lang="scss">
@import "../style-variables.scss";

.TextareaInput {
    &__inputContainer {
        @include input-container;
        height: auto;
        align-items: stretch;
    }

    &__field {
        @include input-field;
        resize: vertical;
        line-height: 1.4;
    }

    &--error {
        @include input-error-state(".TextareaInput__inputContainer");
    }

    &--disabled {
        @include input-disabled-state(".TextareaInput__inputContainer");
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { omitListenerAttrs, toEventListeners } from '@evidentid/vue-commons/utils/attrsListeners';

    import { commonInputProps } from '@/modules/dynamic-form/components/style-inputs/common-input-props';
    import DefaultLayout from '@/modules/dynamic-form/components/style-inputs/default-layout/DefaultLayout.vue';
    import { defaultLayoutSlotForwardingMixin } from '@/modules/dynamic-form/components/style-inputs/default-layout-slot-forwarding.mixin';

    export default defineComponent({
        name: 'TextareaInput',
        components: { DefaultLayout },
        mixins: [ defaultLayoutSlotForwardingMixin ],
        inheritAttrs: false,
        props: {
            ...commonInputProps,
            value: {
                type: String as PropType<string>,
                default: '',
            },
            placeholder: {
                type: String as PropType<string>,
                default: '',
            },
            rows: {
                type: Number as PropType<number>,
                default: 4,
            },
            maxlength: {
                type: Number as PropType<number>,
                default: undefined,
            },
        },
        computed: {
            // Handlers reach the inner element through `v-on="listeners"`. With
            // inheritAttrs left on, Vue 3 binds them to this component's root too, and a
            // native event bubbling out of the inner element then calls the caller twice.
            // Ordinary attributes still reach the root, through `plainAttrs`.
            plainAttrs(): Record<string, unknown> {
                return omitListenerAttrs(this.$attrs);
            },
            // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): Vue 3 removed the separate
            // `$listeners` object -- parent-supplied event handlers now arrive merged
            // into `$attrs` as `onEventName`. Rebuild the bare-event-name shape
            // `v-on="listeners"` expects.
            listeners(): Record<string, (...args: unknown[]) => void> {
                return toEventListeners(this.$attrs);
            },
        },
    });
</script>
